import { create } from "zustand";
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware";
import axios from "axios";
import type { UserRoleValue } from "@campushubai/shared";
import { setUser as setSentryUser } from "@/lib/sentry";
import { queryClient } from "@/lib/query-client";
import { isNative, getApiOrigin } from "@/lib/platform";
import { secureStorage } from "@/lib/secure-storage";
import { disconnectSocket } from "@/lib/socket";
import { readCsrfToken, CSRF_HEADER } from "@/lib/csrf";
import { resetAcademicYear, unpinAcademicYear } from "@/store/academic-year-store";

// Re-exported as `UserRole` so existing call sites in the web app keep
// working. The canonical definition lives in `@campushubai/shared`.
export type UserRole = UserRoleValue;

export interface SchoolRef {
  id: string;
  name: string;
  logo?: string;
  code: string;
  subdomain?: string | null;
  city?: string | null;
  state?: string | null;
}

export interface RoleGrant {
  id: string;
  role: UserRole;
  schoolId: string | null;
  schoolName?: string | null;
  schoolCode?: string | null;
}

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  studentLevel?: "junior" | "senior";
  onboardingComplete?: boolean;
  mustChangePassword?: boolean;
  hasPin?: boolean;
  pinChangeRequired?: boolean;
  // CAMP-2026-0121 — effective School-Profile edit capability from
  // /auth/me. Overseers always true; single-school admins reflect their
  // per-user flag. The School Profile tab hides its Save bar when false.
  canEditSchoolProfile?: boolean;
  school: SchoolRef;
  schools: SchoolRef[];
  roles?: RoleGrant[];
}

interface AuthState {
  user: AuthUser | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  activeSchoolId: string | null;
  isSessionRestored: boolean;
  setSessionRestored: (val: boolean) => void;
  login: (user: AuthUser, token: string, refreshToken?: string) => void;
  logout: () => Promise<void>;
  setActiveSchool: (schoolId: string | null) => void;
  updateUser: (patch: Partial<AuthUser>) => void;
  switchRole: (role: string, schoolId?: string | null) => Promise<void>;
  applySession: (args: {
    user: AuthUser;
    token: string;
    refreshToken?: string | null;
    activeSchoolId?: string | null;
  }) => void;
}

/**
 * Zustand storage adapter that targets the secure backend on native
 * shells (Keychain / EncryptedSharedPreferences) and `localStorage` on
 * the web. We mirror writes into `localStorage` from inside
 * `secureStorage.set` so the synchronous `getItem` path below can still
 * hydrate the store before the React tree mounts. On the very first
 * boot on a real device, the mirror is empty and the user is treated
 * as logged-out — which is correct (we never had a stored token there
 * yet).
 *
 * The Zustand `persist` middleware itself is synchronous, so the
 * `getItem` here returns the mirror value immediately and the async
 * `setItem` is fire-and-forget — Zustand doesn't await it.
 */
export const AUTH_STORAGE_KEY = "campushubai-auth";

/**
 * Where a NEW session is written: `local` survives the browser closing,
 * `session` does not. This is what the login screen's "Keep me signed in"
 * actually controls — unticked, the session lives in `sessionStorage` and is
 * gone with the tab.
 *
 * Reads try `sessionStorage` first and fall back to `localStorage`, so a
 * session written either way rehydrates on boot without knowing which box it
 * went into. Writes clear the other box, so the two can never disagree.
 *
 * Native is unaffected: the Keychain / EncryptedSharedPreferences mirror below
 * is the canonical store there, and the app has its own "Keep me logged in".
 */
export type AuthPersistenceScope = "local" | "session";
let authPersistenceScope: AuthPersistenceScope = "local";
export function setAuthPersistenceScope(scope: AuthPersistenceScope): void {
  authPersistenceScope = scope;
}

const authPersistStorage: StateStorage = {
  getItem(name) {
    try {
      return sessionStorage.getItem(name) ?? localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem(name, value) {
    // Mirror write — keep the chosen web box and the secure store in sync.
    try {
      if (authPersistenceScope === "session") {
        sessionStorage.setItem(name, value);
        localStorage.removeItem(name);
      } else {
        localStorage.setItem(name, value);
        sessionStorage.removeItem(name);
      }
    } catch {
      // ignore
    }
    if (isNative()) {
      // Fire-and-forget — Zustand doesn't await this. The next cold
      // start will pick up the mirror; subsequent reads from
      // `secureStorage.get` see the canonical value.
      void secureStorage.set(name, value);
    }
  },
  removeItem(name) {
    try {
      localStorage.removeItem(name);
      sessionStorage.removeItem(name);
    } catch {
      // ignore
    }
    if (isNative()) {
      void secureStorage.remove(name);
    }
  },
};

/**
 * "platform" is the placeholder `user.school.id` older API flows handed
 * CHAIRMAN / DIRECTOR (their User.schoolId is null). It is not a school: kept
 * as activeSchoolId it read as "a school is picked", so the overseer lost the
 * All-schools consolidated views and every scoped call sent X-School-Id:
 * platform. Every write into activeSchoolId goes through this.
 */
/** Written once per app session; its absence means this is a cold launch. */
const COLD_LAUNCH_KEY = "ch-overseer-launch";

/**
 * Stamp this app session as started, and say whether it already was.
 *
 * The stamp must go down on EVERY hydrate and on every sign-in / school pick —
 * not only when a hydrate finds a school to reset. It used to be written only
 * inside the "overseer with a persisted school" branch, so a session that began
 * on All schools (every fresh sign-in, since a multi-school overseer is seeded
 * with no school) never stamped it: the chairman picked a school, pulled to
 * refresh, and the reload was taken for a cold launch and threw them back to
 * the network tower. Returns true when the session had already started.
 */
export function markAppSessionStarted(): boolean {
  try {
    if (sessionStorage.getItem(COLD_LAUNCH_KEY)) return true;
    sessionStorage.setItem(COLD_LAUNCH_KEY, "1");
    return false;
  } catch {
    // Private mode or blocked storage: treat it as a reload, so a pick is left
    // alone rather than bouncing someone out of their school on every render.
    return true;
  }
}

export function realSchoolId(id: string | null | undefined): string | null {
  return id && id !== "platform" ? id : null;
}

/**
 * The persisted school an overseer was "viewing", checked against the schools
 * they can actually reach. A grant that was revoked, a database that was
 * rebuilt, or a stale snapshot leave an id the API refuses (`X-School-Id` of a
 * school the user isn't a member of is dropped, so the request carries NO school
 * and every school-scoped read answers "No school associated with this
 * account." — one red toast per call, over the top of the open sheet). The
 * client must not keep claiming a scope the server will not honour: fall back
 * to All schools. Only overseers with a known grant list are checked; every
 * other role is returned untouched.
 */
export function reconcileActiveSchool(
  activeSchoolId: string | null,
  user: { role?: string; schools?: Array<{ id: string }> | null } | null | undefined,
): string | null {
  if (!activeSchoolId) return null;
  const overseer = user?.role === "CHAIRMAN" || user?.role === "DIRECTOR";
  const granted = user?.schools ?? [];
  if (overseer && granted.length > 0 && !granted.some((g) => g.id === activeSchoolId)) return null;
  return activeSchoolId;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      activeSchoolId: null,
      isSessionRestored: false,

      setSessionRestored: (val) => set({ isSessionRestored: val }),

      login: (user, token, refreshToken) => {
        setSentryUser({ id: user.id, role: user.role, schoolId: user.school?.id ?? null });
        // Seed activeSchoolId from the user's primary school so consumers
        // that read it directly (e.g. useSchoolSetupStatus) don't think
        // the user is school-less. The school-picker still overrides this
        // for chairmen who manage multiple schools — see setActiveSchool.
        // Without this seed, the setup-required modal/route gate stayed
        // silent for everyone who logged in via the password / OTP flows
        // (only onboarding's applySession was setting activeSchoolId).
        //
        // For multi-school roles (CHAIRMAN / DIRECTOR) the API returns a
        // "platform" sentinel for user.school because their User row has
        // schoolId=null (per multi-school-jwt-rule). Seeding activeSchoolId
        // to "platform" would route the dashboard to PrincipalDashboard
        // and immediately 403 since "platform" isn't a real school. Pin
        // them to null so they land on the aggregate (Chairman/Director)
        // dashboard and pick a real school via the SchoolPicker.
        const isMulti =
          user.role === "CONTROL_CENTER" ||
          user.role === "CHAIRMAN" ||
          user.role === "DIRECTOR";
        // For multi-school roles (CHAIRMAN / DIRECTOR):
        //   - If they have exactly 1 school, auto-seed it so they land directly
        //     on that school's PrincipalDashboard — no multi-school picker needed.
        //   - If they have 2+ schools, leave activeSchoolId null so the
        //     multi-school control tower renders and they pick via SchoolPicker.
        //   - "platform" sentinel from older JWT flows is treated as no school.
        const multiSchoolCount = isMulti ? (user.schools?.length ?? 0) : 0;
        markAppSessionStarted();
        const seedSchoolId = isMulti
          ? multiSchoolCount === 1
            ? (user.schools![0]!.id ?? null)
            : null
          : user.school?.id && user.school.id !== "platform"
            ? user.school.id
            : null;
        set({
          user,
          token,
          refreshToken: refreshToken ?? null,
          isAuthenticated: true,
          activeSchoolId: seedSchoolId,
          isSessionRestored: true,
        });
      },

      logout: async () => {
        // `token` is captured before the local session is cleared so the
        // revocation call below can still prove who is signing out when the
        // refresh token is already gone (CAMP-2026-0833).
        const { refreshToken, token: accessToken } = get();

        // Clear the LOCAL session FIRST and unconditionally. Sign-out must be
        // instant and must never be gated on the network round-trip. On mobile
        // (CAMP-2026-0210) the logout POST could stall — the WebView's local
        // server does not always answer POSTs, and links are flaky — and
        // because the teardown + `signOut()`'s redirect sat *after* an
        // un-timeboxed `await`, the user stayed signed in on the dashboard with
        // no redirect at all. Tear everything down synchronously up front; the
        // server-side revocation below is a timeboxed best-effort.
        setSentryUser(null);
        // Tear down the realtime socket so the next user (or anonymous
        // visitor) doesn't keep receiving events targeted at the user
        // we just logged out. `useSocket()` deliberately does NOT
        // disconnect on hook-cleanup (multiple hooks share the global
        // socket) — logout is the single place we own the teardown.
        disconnectSocket();
        // Clear the React Query cache so a subsequent login never sees
        // data cached from a previous user's session.
        queryClient.clear();
        // CAMP P1 module audit — shared-device safety. The offline GET cache
        // and mutation outbox both live in the SAME IndexedDB store the next
        // logged-in user shares on a kiosk / front-desk PC / driver phone;
        // queryClient.clear() above only touches the in-memory react-query
        // cache, not this persistent layer. Best-effort + fire-and-forget:
        // logout must stay synchronous and must never block on IndexedDB.
        void (async () => {
          try {
            const [{ clearAllOfflineCache }, { clearAllOfflineMutations }] = await Promise.all([
              import("@/lib/offline-cache"),
              import("@/lib/offline-outbox"),
            ]);
            await Promise.all([clearAllOfflineCache(), clearAllOfflineMutations()]);
          } catch {
            // Non-fatal — local auth state is already cleared regardless.
          }
        })();
        set({ user: null, token: null, refreshToken: null, isAuthenticated: false, activeSchoolId: null });
        // The academic year is a per-user view preference, not a device
        // setting. Left behind, the next account to sign in on this device
        // inherits it — and if their school has no data in that year, every
        // year-scoped module renders as "not set up".
        resetAcademicYear();

        // Explicit sign-out clears any stored kiosk credentials so the device
        // stops auto-reauthenticating (the kiosk stays logged in until exactly
        // this point). Dynamic import avoids a module cycle with kiosk-session.
        try {
          const { forgetKioskSession } = await import("@/lib/kiosk-session");
          await forgetKioskSession();
        } catch {
          // Non-fatal — local state already cleared.
        }

        // Best-effort server-side revocation: drop the refresh-token row +
        // clear the httpOnly refresh/CSRF cookies. Uses `apiClient` (NOT the
        // bare `axios` default) so on native the request reaches the real API
        // origin — a relative "/api/v1/auth/logout" would resolve against the
        // WebView's `https://localhost` and never hit the API. Timeboxed so a
        // stalled request can never block sign-out; the route accepts the body
        // refreshToken so revocation works even when the WebView cookie jar
        // doesn't round-trip. The local session is already gone regardless.
        //
        // CAMP-2026-0833 — the call also carries this device's push token so
        // the API deactivates it for the account that is leaving; without it
        // a signed-out phone kept receiving that account's notifications.
        let pushToken: string | null = null;
        try {
          if (isNative()) {
            const { getNativeDevicePushToken } = await import("@/lib/native-push");
            pushToken = getNativeDevicePushToken();
          } else {
            const { takeWebPushTokenForLogout } = await import("@/hooks/use-web-push");
            pushToken = takeWebPushTokenForLogout();
          }
        } catch {
          // Non-fatal — the session is still revoked below.
        }
        try {
          const { apiClient } = await import("@/api/client");
          await apiClient.post(
            "/auth/logout",
            {
              ...(refreshToken ? { refreshToken } : {}),
              ...(pushToken ? { pushToken } : {}),
            },
            accessToken
              ? { timeout: 4000, headers: { Authorization: `Bearer ${accessToken}` } }
              : { timeout: 4000 },
          );
        } catch {
          // Ignore logout API errors — local session already cleared.
        }
      },

      setActiveSchool: (schoolId) => {
        markAppSessionStarted();
        set({ activeSchoolId: realSchoolId(schoolId) });
        queryClient.clear();
        // Different tenant, different populated years. Un-pin the year so
        // the setup-status probe is free to retarget it for the school we
        // just switched into instead of carrying the old school's year over.
        unpinAcademicYear();
      },

      updateUser: (patch) =>
        set((s) => (s.user ? { user: { ...s.user, ...patch } } : s)),

      switchRole: async (role: string, schoolId?: string | null) => {
        const { token } = get();
        if (!token) throw new Error("Not authenticated — please log in again");

        // Build absolute URL so the request goes to the real API, not the
        // web app's CloudFront distribution (which only allows GET/HEAD).
        const apiOrigin = getApiOrigin();
        const switchRoleUrl = apiOrigin
          ? `${apiOrigin.replace(/\/+$/, "")}/api/v1/auth/switch-role`
          : "/api/v1/auth/switch-role";

        const attemptSwitch = async (bearerToken: string, overrideSchoolId?: string | null) => {
          // withCredentials sends the refresh cookie, so the API's CSRF check
          // applies — echo the token (read fresh each attempt: the refresh
          // retry below rotates it).
          const csrf = readCsrfToken();
          const res = await axios.post(
            switchRoleUrl,
            { role, schoolId: overrideSchoolId !== undefined ? overrideSchoolId : (schoolId ?? null) },
            {
              headers: { Authorization: `Bearer ${bearerToken}`, ...(csrf ? { [CSRF_HEADER]: csrf } : {}) },
              withCredentials: true,
            },
          );
          const data = res.data?.data;
          if (!data?.accessToken || !data?.user) {
            throw new Error("Unexpected response from server — please try again");
          }
          return data;
        };

        try {
          let data: Awaited<ReturnType<typeof attemptSwitch>>;
          try {
            data = await attemptSwitch(token);
          } catch (firstErr) {
            // On 403, the session may be stale — the admin may have granted the
            // role after this token was minted. Refresh once and retry so the
            // server sees the current UserRoleGrant state.
            if (axios.isAxiosError(firstErr) && firstErr.response?.status === 403) {
              const { refreshAccessToken } = await import("@/api/client");
              const freshToken = await refreshAccessToken();
              // After refresh, pick up the updated schoolId for this role
              // (the stale session may have had null/wrong schoolId).
              const freshGrant = get().user?.roles?.find((r) => r.role === role);
              const freshSchoolId = freshGrant?.schoolId ?? schoolId ?? null;
              data = await attemptSwitch(freshToken, freshSchoolId);
            } else {
              throw firstErr;
            }
          }

          setSentryUser({
            id: data.user.id,
            role: data.user.role,
            schoolId: data.user.school?.id ?? null,
          });

          // Clear query cache so all dashboard data refetches for the new role
          queryClient.clear();

          set({
            user: data.user,
            token: data.accessToken,
            refreshToken: data.refreshToken ?? get().refreshToken ?? null,
            isAuthenticated: true,
            activeSchoolId: realSchoolId(data.activeSchoolId ?? data.user.school?.id ?? null),
            isSessionRestored: true,
          });
        } catch (err) {
          console.error("[auth] switch-role failed", err);
          // Lift the API error message so the UI can show it directly.
          if (axios.isAxiosError(err)) {
            const status = err.response?.status;
            if (status === 403) {
              // Refresh roles so the stale dropdown entry disappears.
              try {
                const { refreshAccessToken } = await import("@/api/client");
                await refreshAccessToken();
              } catch { /* ignore */ }
              const apiMsg = (err.response?.data as { error?: { message?: string } })?.error?.message;
              throw new Error(apiMsg ?? "Role not available — ask your admin to re-grant it");
            }
            if (status === 401) {
              throw new Error("Your session has expired — please refresh the page");
            }
            const apiMsg = (err.response?.data as { error?: { message?: string } })?.error?.message;
            if (apiMsg) throw new Error(apiMsg);
          }
          // Re-throw whatever we have (includes the "Unexpected response" error above)
          throw err;
        }
      },

      applySession: ({ user, token, refreshToken, activeSchoolId }) => {
        setSentryUser({ id: user.id, role: user.role, schoolId: user.school?.id ?? null });
        set((s) => ({
          user,
          token,
          refreshToken: refreshToken ?? s.refreshToken ?? null,
          isAuthenticated: true,
          activeSchoolId: realSchoolId(
            activeSchoolId !== undefined ? activeSchoolId : s.activeSchoolId,
          ),
          isSessionRestored: true,
        }));
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => authPersistStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        activeSchoolId: state.activeSchoolId,
      }),
      // A session persisted before the sentinel was normalised can still
      // carry activeSchoolId "platform" — drop it on rehydrate.
      //
      // A multi-school overseer also starts each COLD LAUNCH on the network
      // overview rather than on whichever school they last opened. Signing in
      // already seeds `activeSchoolId: null` for them "so the multi-school
      // control tower renders" — but the pick is persisted, so after a single
      // tap every later launch resumed that school, and the network view (the
      // one carrying group health, and the reason the role exists at all) was
      // never seen again unless they went looking for it. Reported as
      // "chairman dashboard did not show me school health".
      //
      // Cold launch ONLY, deliberately: a reload or a refresh keeps the school
      // you are working in. sessionStorage is what tells the two apart — it
      // survives a refresh and dies with the app.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<AuthState>;
        let activeSchoolId = realSchoolId(p.activeSchoolId ?? current.activeSchoolId);
        const u = p.user ?? current.user;
        const overseer = u?.role === "CHAIRMAN" || u?.role === "DIRECTOR";
        const resumed = markAppSessionStarted();
        if (!resumed && overseer && (u?.schools?.length ?? 0) > 1) {
          activeSchoolId = null;
        }
        activeSchoolId = reconcileActiveSchool(activeSchoolId, u);
        return { ...current, ...p, activeSchoolId };
      },
    },
  ),
);

/**
 * Re-seed the `localStorage` mirror from the hardware-backed store on native.
 *
 * Zustand's `persist` is synchronous, so it can only hydrate from the mirror
 * (see `authPersistStorage` above). But the mirror is the volatile copy: an
 * Android WebView's localStorage is evicted under storage pressure, wiped by
 * "Clear cache", and lost across some app updates — while Keychain /
 * EncryptedSharedPreferences keep the session intact. When that happened the
 * app booted signed-out and demanded a fresh login even though a perfectly
 * good session was sitting in the secure store.
 *
 * So on native cold start, if the mirror is missing but the secure copy exists,
 * copy it back and re-run hydration BEFORE the first render. Must be awaited by
 * the entry point — otherwise the router decides what to draw against
 * `isAuthenticated: false` and bounces the user to /login.
 */
export async function restoreNativeSessionMirror(): Promise<void> {
  if (!isNative()) return;
  try {
    if (localStorage.getItem(AUTH_STORAGE_KEY)) return; // mirror is intact
    const secure = await secureStorage.get(AUTH_STORAGE_KEY);
    if (!secure) return;
    localStorage.setItem(AUTH_STORAGE_KEY, secure);
    await useAuthStore.persist.rehydrate();
    console.info("[auth] restored session mirror from the secure store");
  } catch {
    // Best-effort: a failure here just means the user logs in again.
  }
}

/**
 * Strict, user-initiated sign-out. Clears server + local session, then does
 * a HARD navigation (not SPA) to /login.
 *
 * Why hard navigation: an SPA `navigate("/login")` leaves the previous
 * authenticated pages in history, and the browser's back-forward cache
 * (bfcache) can restore them with a frozen, still-"authenticated" JS heap —
 * so Back lands the signed-out user back on the dashboard. `location.replace`
 * tears down the whole app + drops the current history entry, and the
 * `pageshow` bfcache guard in App.tsx reloads any older restored page whose
 * persisted session is gone. Together they make Back-after-signout safe.
 */
export async function signOut(): Promise<void> {
  try {
    await useAuthStore.getState().logout();
  } catch {
    // logout() already swallows API errors and clears state regardless.
  } finally {
    if (typeof window !== "undefined") {
      window.location.replace("/login");
    }
  }
}
